import FormControl from '@mui/material/FormControl';
import React from 'react';
import {
  Autocomplete,
  Box,
  FormHelperText,
  FormLabel,
  TextField,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import InputCheckbox from './CheckBox';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Images from '../Utils/images';

interface Props {
  id: string;
  labelText?: string;
  placeHolderText?: string;
  name?: string;
  error?: boolean;
  helperText?: string;
  defaultValue?: string | any;
  disabled?: boolean;
  value?: any;
  onChange?: any;
  options: any;
  control?: null | any;
  setValue?: any;
  isError?: boolean;
  isCountryField?: boolean;
  multiple?: boolean;
  isCheckbox?: boolean;
  requiredField?: boolean;
  labelKey?: string;
  limit?: number;
  clearIcon?: boolean;
  disableClearable?: boolean;
  onInputChange?: any;
  highLighted?: boolean;
  isDisableText?: boolean;
  showFrontIcon?: React.ReactNode;
  isLocationPurpose?: boolean;
  order?: boolean;
  addOptionText?: string;
  onAdd?: () => void;
  // If true and `multiple` is false, this component will:
  // - display the first item when the stored value is an array
  // - store selection back as an array: `[newValue]` (or `[]` when cleared)
  storeAsArray?: boolean;
}
/**
 * Component that renders an autocomplete selection field with various customization options.
 * @example
 * AutocompleteSelect({
 *   id: 'country-select',
 *   labelText: 'Country',
 *   placeHolderText: 'Select a country',
 *   name: 'country',
 *   options: countryOptions,
 *   control: someControl,
 *   setValue: someSetValueFunction
 * })
 * @param {Props} props - Object containing component properties.
 * @param {string} props.id - Unique identifier for the component.
 * @param {string} props.labelText - Label text to display above the autocomplete field.
 * @param {string} props.placeHolderText - Placeholder text inside the autocomplete input.
 * @param {string} [props.name=''] - Name attribute for the field.
 * @param {boolean} props.error - Flag to indicate if there's an error.
 * @param {string} props.helperText - Additional text to aid the user.
 * @param {any} props.defaultValue - Default selected value for the autocomplete.
 * @param {boolean} props.disabled - Flag to disable the input field.
 * @param {Array} props.options - Options available for selection.
 * @param {any} props.control - Method or object to manage the component's state.
 * @param {function} props.setValue - Function to set or update the selected value.
 * @param {boolean} [props.isCountryField=false] - Determines if the options are country-specific.
 * @param {boolean} props.multiple - Allows multiple selections if true.
 * @param {boolean} props.isCheckbox - Renders each option as a checkbox if true.
 * @param {boolean} props.requiredField - Indicates if the field is mandatory.
 * @param {function} props.onChange - Callback function on value change.
 * @param {any} props.value - Current selected value(s).
 * @param {string} props.labelKey - Key to access the label within an option object.
 * @param {number} [props.limit=1] - Limits the number of tags shown in the input field.
 * @param {boolean} [props.clearIcon=false] - Displays an icon to clear the input if true.
 * @param {boolean} [props.disableClearable=false] - Prevents clearing the input if true.
 * @param {function} props.onInputChange - Callback function for input changes.
 * @param {boolean} props.highLighted - Applies a CSS class for highlight if true.
 * @param {boolean} props.isDisableText - Disables text input if true.
 * @returns {JSX.Element} Rendered Autocomplete component.
 * @description
 *   - Integrates with `react-hook-form` using the `Controller` component.
 *   - Allows customized rendering of options, including country flags and checkboxes.
 *   - Implements blur logic after selection to improve user experience.
 */
export default function AutocompleteSelect(props: Props) {
  const {
    id,
    labelText,
    placeHolderText,
    name = '',
    error,
    helperText,
    defaultValue,
    disabled,
    options,
    control,
    setValue,
    isCountryField = false,
    multiple,
    isCheckbox,
    requiredField,
    onChange,
    value,
    labelKey,
    limit = 1,
    clearIcon = false,
    disableClearable = false,
    onInputChange,
    highLighted,
    isDisableText,
    showFrontIcon,
    isLocationPurpose,
    addOptionText,
    onAdd,
    storeAsArray,
  } = props;

  const addOptionEntry =
    addOptionText && onAdd && labelKey
      ? { _id: '__ADD__', [labelKey]: addOptionText, isAddOption: true }
      : null;

  // MUI Autocomplete expects `options` to be an array (it calls `options.filter` internally).
  // Some callers briefly pass an object/undefined during API hydration → crash.
  const safeOptions = Array.isArray(options) ? options : [];
  const optionsWithAdd = addOptionEntry ? [addOptionEntry, ...safeOptions] : safeOptions;

  const isAddOption = (option: any) =>
    option?.isAddOption === true || option?._id === '__ADD__';

  const [open, setOpen] = React.useState(false);
  const showAddOption = !!addOptionEntry;

  // console.log(options, 'reporters', labelText);

  return (
    <FormControl
      fullWidth
      // className='customAutocomplete'
      className={`customAutocomplete ${highLighted ? 'highLighted' : ''}`}
      error={error ? true : false}
    >
      {labelText && (
        <FormLabel className="formLabel">
          {labelText} {requiredField ? <sup>*</sup> : null}{' '}
        </FormLabel>
      )}

      {control ? (
        <Controller
          name={name}
          control={control}
          defaultValue={defaultValue ? defaultValue : null}
          render={({ field }) => {
            const fieldValueForUi =
              !multiple && storeAsArray && Array.isArray(field.value)
                ? field.value?.[0] ?? null
                : field.value;

            return (
              <Autocomplete
                {...(showAddOption && {
                  open,
                  onOpen: () => setOpen(true),
                  onClose: () => setOpen(false),
                })}
                limitTags={limit}
                clearIcon
                {...field}
                value={fieldValueForUi}
                disableClearable={disableClearable}
                disablePortal
                id={id || 'combo-box-demo'}
                size="medium"
                placeholder={placeHolderText}
                disabled={disabled}
                popupIcon={<KeyboardArrowDownIcon />}
                onInputChange={onInputChange ? onInputChange : undefined}
                getOptionLabel={
                  isCountryField
                    ? option => {
                        return option ? option.name : '';
                      }
                    : labelKey
                    ? option => {
                        return option ? option[labelKey] : '';
                      }
                    : undefined
                }
                filterOptions={
                  showAddOption && labelKey
                    ? (opts, state) => {
                        const addOpt = opts.find((o: any) => isAddOption(o));
                        const rest = opts.filter((o: any) => !isAddOption(o));
                        const input = state.inputValue?.toLowerCase() ?? '';
                        const filtered = input
                          ? rest.filter((opt: any) =>
                              (opt[labelKey] ?? '')
                                .toLowerCase()
                                .includes(input),
                            )
                          : rest;
                        return addOpt ? [addOpt, ...filtered] : filtered;
                      }
                    : undefined
                }
                defaultValue={defaultValue}
                disableCloseOnSelect={multiple}
                isOptionEqualToValue={
                  labelKey
                    ? (option, val) =>
                        isAddOption(option)
                          ? false
                          : option?.[labelKey] === val?.[labelKey]
                    : undefined
                }
                renderOption={
                  isCountryField
                    ? (props, option) => (
                        <Box
                          component="li"
                          sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                          {...props}
                        >
                          <img
                            loading="lazy"
                            width="20"
                            srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                            src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                            alt="Country Flag Logo"
                          />
                          {option.name} ({option.code})
                        </Box>
                      )
                    : isCheckbox
                    ? (props, options, { selected }) => {
                        return (
                          <li {...props} key={id}>
                            {isCheckbox ? (
                              <InputCheckbox
                                id={`check_${name}`}
                                name={`check_${name}`}
                                checked={selected}
                              />
                            ) : (
                              ''
                            )}
                            {labelKey ? options[labelKey] : options}
                          </li>
                        );
                      }
                    : labelKey
                    ? (props, option) => {
                        if (showAddOption && isAddOption(option)) {
                          const { key: _key, ...liProps } = props as any;
                          return (
                            <li
                              key={option._id}
                              {...liProps}
                              onMouseDown={e => e.preventDefault()}
                              onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                onAdd?.();
                                setOpen(false);
                              }}
                            >
                              <div className="add_in_select">
                                <img src={Images.ADD_CIRCLE} alt="" />
                                {option[labelKey]}
                              </div>
                            </li>
                          );
                        }
                        return (
                          <li {...props} key={id}>
                            {option[labelKey]}
                          </li>
                        );
                      }
                    : undefined
                }
                options={optionsWithAdd}
                onChange={
                  onChange
                    ? (event, newValue: any, reason) => {
                        if (isAddOption(newValue)) {
                          onAdd?.();
                          return;
                        }
                        onChange(event, newValue, reason);

                        // Force blur after value selection
                        setTimeout(() => {
                          const activeEl =
                            document.activeElement as HTMLElement;
                          if (activeEl && typeof activeEl.blur === 'function') {
                            activeEl.blur();
                          }
                        }, 100);
                      }
                    : (event, newValue: any, reason) => {
                        if (isAddOption(newValue)) {
                          onAdd?.();
                          return;
                        }
                        // console.log(newValue, 'newValue');
                        if (!multiple && storeAsArray) {
                          setValue(name, newValue ? [newValue] : []);
                        } else {
                          setValue(name, newValue);
                        }

                        // Force blur after value selection
                        setTimeout(() => {
                          const activeEl =
                            document.activeElement as HTMLElement;
                          if (activeEl && typeof activeEl.blur === 'function') {
                            activeEl.blur();
                          }
                        }, 100);
                      }
                }
                sx={{ width: '100%' }}
                renderInput={params => (
                  <TextField
                    placeholder={placeHolderText}
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      type: 'search',
                      readOnly: true,
                      startAdornment: showFrontIcon ? showFrontIcon : null,
                    }}
                    error={error ? true : false}
                  />
                )}
                multiple={multiple}
                ChipProps={{
                  deleteIcon: <img src={Images.CHIP_DELETE} alt="" />,
                }}
              />
            );
          }}
        />
      ) : (
        <Autocomplete
          // open
          // disableCloseOnSelect
          clearIcon
          limitTags={limit}
          disablePortal
          id="combo-box-demo"
          size="medium"
          options={options}
          placeholder={placeHolderText}
          onChange={onChange}
          sx={{ width: '100%' }}
          value={value}
          disableCloseOnSelect={multiple}
          popupIcon={<KeyboardArrowDownIcon />}
          multiple={multiple}
          disabled={disabled}
          isOptionEqualToValue={
            labelKey
              ? (option, value) => {
                  return option[labelKey] === value[labelKey];
                }
              : undefined
          }
          renderOption={
            isCheckbox
              ? (props, options, { selected }) => {
                  return (
                    <li {...props} key={id}>
                      {isCheckbox ? (
                        <InputCheckbox
                          id={`check_${name}`}
                          name={`check_${name}`}
                          checked={selected}
                        />
                      ) : (
                        ''
                      )}
                      {labelKey ? options[labelKey] : options}
                    </li>
                  );
                }
              : isLocationPurpose
              ? (props, option) => {
                  return (
                    <li
                      {...props}
                      key={option.value}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                      }}
                    >
                      <span className="font-medium">{option.label}</span>
                      {option.locationPurpose?.length > 0 && (
                        <small className="text-gray_600">
                          {option.locationPurpose.join(', ')}
                        </small>
                      )}
                    </li>
                  );
                }
              : labelKey
              ? (props, option) => {
                  return (
                    <li {...props} key={id}>
                      {option[labelKey]}
                    </li>
                  );
                }
              : undefined
          }
          getOptionLabel={
            labelKey ? option => (option ? option[labelKey] : '') : undefined
          }
          renderInput={params => (
            <TextField
              placeholder={placeHolderText}
              {...params}
              error={error ? true : false}
            />
          )}
          ChipProps={{
            deleteIcon: <img src={Images.CHIP_DELETE} alt="" />,
          }}
        />
      )}

      <FormHelperText className={error ? 'Mui-error' : ''}>
        {helperText}
      </FormHelperText>
    </FormControl>
  );
}
